import { useEffect, useState } from 'react';
import type { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import type { ServiceCode } from '../../../../../../common/enums/serviceCode';
import type { ServiceType } from '../../../../../../common/enums/serviceType';
import type { ValidationErrors } from '../../../../../utils/validation';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { Link, ReadOnlyInputText } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ServiceOrganizationProviderSearchInNetworkModal } from '../../../../elements/ServiceOrganizationProviderSearchInNetworkModal';
import { ServiceOrganizationProviderSelection } from '../../../../elements/ServiceOrganizationProviderSelection';

interface IServiceOrganizationProviderSelectionProps {
    addOctPracticesInformation?: boolean;
    checkRecipientIsOwnOrganization?: boolean;
    errors: ValidationErrors;
    excludeNonOctPractices?: boolean;
    initialOrganizationId: string | null;
    initialPractitionerId: string | null;
    isExaminationRequest?: boolean;
    isExternalHolter?: boolean;
    isReadOnlyIfSingleServiceOrganizationProvider?: boolean;
    onChange: (organizationId: string | null, practitionerId: string | null) => Promise<void>;
    onDoValidate?: () => void;
    requesterOrganizationId?: string;
    roleOfPractitionersInList?: PractitionerRoleInRequest;
    searchInNetwork?: boolean;
    serviceCode?: ServiceCode;
    serviceProviderServiceId: string;
    serviceType?: ServiceType;
}

export function ServiceOrganizationProvider(props: IServiceOrganizationProviderSelectionProps) {
    const [showAddOrganizationNationWideModal, setShowAddOrganizationNationWideModal] = useState(false);
    const [recipientIsOwnOrganization, setRecipientIsOwnOrganization] = useState<boolean>();
    const [organizationLabel, setOrganizationLabel] = useState<string>();

    useEffect(() => {
        if (props.checkRecipientIsOwnOrganization && props.requesterOrganizationId) {
            checkRecipientIsOwnOrganization();
        }
    }, []);

    return (
        <HorizontalFormGroup
            hasErrors={
                props.errors.has('selectedProcedureProviderOrganizationId') ||
                props.errors.has('selectedDiagnosticReportProviderOrganizationId')
            }
        >
            <FormLabel isRequired={!props.isReadOnlyIfSingleServiceOrganizationProvider}>
                {translateKey('request.sendTo')}
            </FormLabel>

            <InputContainer>
                {recipientIsOwnOrganization && organizationLabel && <ReadOnlyInputText value={organizationLabel} />}

                {!recipientIsOwnOrganization && (
                    <ServiceOrganizationProviderSelection
                        addOctPracticesInformation={props.addOctPracticesInformation}
                        error={
                            props.errors.get('selectedProcedureProviderOrganizationId') ||
                            props.errors.get('selectedDiagnosticReportProviderOrganizationId')
                        }
                        excludeNonOctPractices={props.excludeNonOctPractices}
                        initialOrganizationId={props.initialOrganizationId}
                        initialPractitionerId={props.initialPractitionerId}
                        isExaminationRequest={props.isExaminationRequest}
                        isExternalHolter={props.isExternalHolter}
                        isReadOnlyIfSingleServiceOrganizationProvider={
                            props.isReadOnlyIfSingleServiceOrganizationProvider
                        }
                        onChange={props.onChange}
                        onDoValidate={props.onDoValidate}
                        roleOfPractitionersInList={props.roleOfPractitionersInList}
                        serviceCode={props.serviceCode}
                        serviceProviderServiceId={props.serviceProviderServiceId}
                        serviceType={props.serviceType}
                    />
                )}

                {props.searchInNetwork && (
                    <>
                        <Link
                            onClick={(e) => {
                                e.preventDefault();
                                setShowAddOrganizationNationWideModal(true);
                            }}
                            to="#"
                        >
                            Zoeken in voorkeurslijst en landelijk netwerk
                        </Link>
                        {showAddOrganizationNationWideModal && (
                            <ServiceOrganizationProviderSearchInNetworkModal
                                initialOrganizationId={props.initialOrganizationId}
                                initialPractitionerId={props.initialPractitionerId}
                                itemsPerPage={7}
                                onChange={async (organizationId: string | null, practitionerId: string | null) => {
                                    await props.onChange(organizationId, practitionerId);
                                    setShowAddOrganizationNationWideModal(false);
                                }}
                                onClose={async () => setShowAddOrganizationNationWideModal(false)}
                                serviceId={props.serviceProviderServiceId}
                                serviceType={props.serviceType}
                            />
                        )}
                    </>
                )}
            </InputContainer>
        </HorizontalFormGroup>
    );

    async function checkRecipientIsOwnOrganization() {
        let url = `/api/serviceOrganizationSettings?serviceId=${props.serviceProviderServiceId}&organizationId=${props.requesterOrganizationId}&isActive=true`;
        const serviceOrganizationSettings = await fetchJson(url, 'GET');
        const hasRecipientIsOwnOrganizationEnabled =
            serviceOrganizationSettings.json?.items?.length &&
            serviceOrganizationSettings.json.items[0].recipientIsOwnOrganization;
        let organizationHasActiveServiceProviders = false;

        if (hasRecipientIsOwnOrganizationEnabled) {
            url = `/api/serviceProviders?serviceId=${props.serviceProviderServiceId}&organizationId=${props.requesterOrganizationId}&isActive=true`;
            const serviceProvidersResult = await fetchJson(url, 'GET');

            if (serviceProvidersResult.json?.total > 0) {
                organizationHasActiveServiceProviders = true;
                await props.onChange(props.requesterOrganizationId!, null);
                setOrganizationLabel(serviceProvidersResult.json.items[0].employment.organization.name);
            }
        }
        setRecipientIsOwnOrganization(hasRecipientIsOwnOrganizationEnabled && organizationHasActiveServiceProviders);
    }
}
